/**
 * PropertyOverview Component — CTL Management override
 *
 * Adapted from Clackamas Trails: photo_gallery collection binding (instead of
 * 10 fixed photo slot groups) so the carousel and lightbox use the full
 * property photo collection. Only the current slide ±1 are mounted for
 * lighter loading.
 *
 * State-gated licensing / management description:
 *
 *   CA  → "Managed by CTL Management, Inc. License {custom_company_license_number}"
 *   WA  → "Managed by CTL Management, Inc.  This community does not accept
 *           comprehensive reusable tenant screening reports."
 *   OR  → "Managed by CTL Management, Inc."
 *   else → (nothing rendered)
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useContentToken,
  useCollectionBinding,
  usePropertyField,
  EditableToken,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';
// @ts-ignore
import { ChevronLeftIcon, ChevronRightIcon, CameraIcon } from '@vibesites/assets';
import { PhotoGallery } from './PhotoGallery';

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
}

// Gallery image sizes descriptor — matches 2-column layout (50% on desktop)
const GALLERY_SIZES = '(max-width: 768px) 100vw, (max-width: 1400px) 50vw, 700px';

function wrapDist(i: number, current: number, length: number): number {
  if (length <= 0) return 0;
  const d = Math.abs(i - current);
  return Math.min(d, length - d);
}

export function PropertyOverview() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const sectionTitle = useContentToken('propertyoverview.title');
  const propertyName = useContentToken('propertyoverview.property_name');
  const phone = useContentToken('propertyoverview.phone');
  const address = useContentToken('propertyoverview.address');

  // Property fields for state-gated licensing description
  const propertyState = usePropertyField('property_address_state');
  const licenseNumber = usePropertyField('custom_company_license_number');

  const galleryPhotos = useCollectionBinding<GalleryPhoto>('propertyoverview.photos', {
    collection: 'photo_gallery',
  });

  // Build display gallery from the collection, filtering out empty entries
  const displayGallery = useMemo(() => {
    return (galleryPhotos || [])
      .map((photo, i) => ({
        src: photo.image || photo.original_url || '',
        alt: photo.alt_text || photo.title || `Property photo ${i + 1}`,
        description: photo.description || '',
      }))
      .filter((p) => p.src);
  }, [galleryPhotos]);

  const galleryLength = displayGallery.length;

  // Keep current slide in range when the bound collection changes
  useEffect(() => {
    if (galleryLength === 0) {
      setCurrentSlide(0);
      return;
    }
    setCurrentSlide((prev) => Math.min(prev, galleryLength - 1));
  }, [galleryLength]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % galleryLength);
  }, [galleryLength]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + galleryLength) % galleryLength);
  }, [galleryLength]);

  const lightboxPhotos = displayGallery.map((p) => ({ src: p.src, alt: p.alt }));

  // Derive the state-gated licensing text
  const state = (propertyState || '').toUpperCase();

  return (
    <section className="bg-background py-16 md:py-24" id="overview" style={{ scrollMarginTop: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Content column */}
        <div className="flex flex-col gap-6">
          <EditableToken path="propertyoverview.title" type="text" label="Section Title" as="div">
            <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-bold font-heading text-foreground leading-tight uppercase m-0">
              {sectionTitle}{' '}
              <EditableToken path="propertyoverview.property_name" type="text" label="Property Name" as="span">
                <span className="text-foreground">{propertyName}</span>
              </EditableToken>
              , your new home.
            </h2>
          </EditableToken>

          {/* Phone */}
          <EditableToken
            path="propertyoverview.phone"
            type="phone"
            label="Phone Number"
            as="div"
            style={{ display: 'inline-block' }}
          >
            <a href={`tel:${phone}`} className="text-foreground no-underline text-2xl font-medium font-accent">
              {phone}
            </a>
          </EditableToken>

          {/* Address */}
          <EditableToken
            path="propertyoverview.address"
            type="text"
            label="Address"
            as="div"
            style={{ display: 'inline-block' }}
          >
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground text-base underline"
            >
              {address}
            </a>
          </EditableToken>

          {/* Description (richtext) */}
          <RichTextContent
            path="propertyoverview.description"
            label="Description"
            as="p"
            className="text-base leading-relaxed text-foreground m-0"
          />

          {/* State-gated property licensing / management description */}
          {state === 'CA' && (
            <p className="text-sm leading-relaxed text-muted-foreground m-0">
              Managed by CTL Management, Inc.{licenseNumber ? ` License ${licenseNumber}` : ''}
            </p>
          )}
          {state === 'WA' && (
            <p className="text-sm leading-relaxed text-muted-foreground m-0">
              Managed by CTL Management, Inc.&nbsp;&nbsp;This community does not accept comprehensive reusable tenant screening reports.
            </p>
          )}
          {state === 'OR' && (
            <p className="text-sm leading-relaxed text-muted-foreground m-0">
              Managed by CTL Management, Inc.
            </p>
          )}
        </div>

        {/* Gallery column */}
        <div className="flex flex-col gap-4">
          <EditableCollectionBinding bindingPath="propertyoverview.photos" label="Gallery Photos">
            {displayGallery.length > 0 ? (
              <>
                <div className="relative rounded-lg overflow-hidden aspect-[16/10] bg-neutral-900 group">
                  {/* Mount current ±1 only — neighbors load eagerly so the next
                      crossfade is already warm; far slides stay unloaded. */}
                  {displayGallery.map((photo, i) => {
                    if (wrapDist(i, currentSlide, galleryLength) > 1) return null;
                    return (
                      <div
                        key={i}
                        className="absolute inset-0 z-[2]"
                        style={{
                          opacity: i === currentSlide ? 1 : 0,
                          transition: 'opacity 0.35s ease',
                          pointerEvents: i === currentSlide ? 'auto' : 'none',
                        }}
                      >
                        <VibesitesImage
                          url={photo.src}
                          alt={photo.alt || 'Property image'}
                          fill
                          objectFit="cover"
                          sizes={GALLERY_SIZES}
                          quality={80}
                          priority
                        />
                      </div>
                    );
                  })}

                  {/* Full-area lightbox trigger — above images (z-[3]), below nav buttons (z-[5]).
                      A real <button> here avoids nesting interactive controls inside a
                      role="button" div, which violates WCAG 4.1.1 and the HTML spec. */}
                  <button
                    className="absolute inset-0 z-[3] w-full h-full bg-transparent border-none p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
                    onClick={() => setLightboxOpen(true)}
                    aria-label="View photo gallery"
                  />

                  {/* "View gallery" badge */}
                  <div className="absolute top-3 right-3 z-[4] flex items-center gap-1.5 bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <CameraIcon size={13} />
                    <span>View gallery</span>
                  </div>

                  {/* Overlay navigation buttons */}
                  {displayGallery.length > 1 && (
                    <>
                      <button
                        className="absolute top-1/2 left-3 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-sm cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-white hover:shadow-md border-none opacity-70 md:opacity-0 md:group-hover:opacity-100 text-foreground"
                        onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                        aria-label="Previous image"
                      >
                        <ChevronLeftIcon size={18} />
                      </button>
                      <button
                        className="absolute top-1/2 right-3 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-sm cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-white hover:shadow-md border-none opacity-70 md:opacity-0 md:group-hover:opacity-100 text-foreground"
                        onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                        aria-label="Next image"
                      >
                        <ChevronRightIcon size={18} />
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="aspect-[16/10] bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                <span>No gallery images available</span>
              </div>
            )}
          </EditableCollectionBinding>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxPhotos.length > 0 && (
        <PhotoGallery
          photos={lightboxPhotos}
          open={lightboxOpen}
          initialIndex={currentSlide}
          onClose={() => setLightboxOpen(false)}
        />
      )}

    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Welcome to',
    },
    'propertyoverview.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Your New Home',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue: 'Discover modern living at its finest.',
    },
    'propertyoverview.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '555-555-5555',
    },
    'propertyoverview.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street',
    },
  },
  collectionBindings: {
    'propertyoverview.photos': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description: 'Photos shown in the overview carousel and lightbox.',
    },
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
